import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "../lib/trpc.js";
import { ErrorBox, Spinner } from "../components/ui.js";
import Markdown from "../components/Markdown.js";
import { formatDate, tagLabel } from "../lib/format.js";
import { useSession } from "../lib/session.js";

export default function Article() {
  const { slug } = useParams();
  const trpc = useTRPC();
  const { isCommissioner } = useSession();
  const query = useQuery(trpc.articles.bySlug.queryOptions({ slug: slug! }));

  if (query.isLoading) return <Spinner />;
  if (query.error) return <ErrorBox error={query.error} />;
  if (!query.data) return null;

  const a = query.data;

  return (
    <article className="mx-auto max-w-3xl">
      <nav className="mb-6 flex items-center justify-between text-sm">
        <Link to="/news" className="link">
          ← All news
        </Link>
        {isCommissioner && (
          <Link to={`/admin/articles/${a.slug}`} className="btn-secondary py-1.5">
            Edit
          </Link>
        )}
      </nav>

      <header className="mb-8 border-b border-field-800 pb-6">
        <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-wider text-chalk-500">
          {a.publishedAt ? (
            <time>{formatDate(a.publishedAt)}</time>
          ) : (
            /* The archive records no publication dates; say so rather than
               showing a date the league never actually had. */
            <span title="The league archive doesn't record when this was written">
              From the archive
            </span>
          )}
          {a.seasonName && (
            <Link to={`/season?season=${a.seasonSlug}`} className="link">
              {a.seasonName}
            </Link>
          )}
          {a.status === "draft" && (
            <span className="chip border-flag-500/40 text-flag-400">Draft</span>
          )}
        </div>
        <h1 className="mt-3 text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
          {a.title}
        </h1>
        {a.subtitle && (
          <p className="mt-3 text-lg italic text-chalk-400">{a.subtitle}</p>
        )}
        {a.authorName && (
          <p className="mt-4 text-sm text-chalk-500">By {a.authorName}</p>
        )}
        {a.tags && a.tags.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {/* Each tag opens the news page filtered to it. */}
            {a.tags.map((t) => (
              <Link
                key={t}
                to={`/news?tag=${encodeURIComponent(t)}`}
                className="chip hover:border-blitz-500 hover:text-blitz-400"
              >
                {tagLabel(t)}
              </Link>
            ))}
          </div>
        )}
      </header>

      <Markdown>{a.body}</Markdown>

      <nav className="mt-12 grid gap-3 border-t border-field-800 pt-6 sm:grid-cols-2">
        {a.older && (
          <Link
            to={`/news/${a.older.slug}`}
            className="card p-4 transition hover:border-field-600"
          >
            <div className="text-[11px] uppercase tracking-wider text-chalk-500">
              Older
            </div>
            <div className="mt-1 text-sm font-semibold">{a.older.title}</div>
          </Link>
        )}
        {a.newer && (
          <Link
            to={`/news/${a.newer.slug}`}
            className="card p-4 text-right transition hover:border-field-600 sm:col-start-2"
          >
            <div className="text-[11px] uppercase tracking-wider text-chalk-500">
              Newer
            </div>
            <div className="mt-1 text-sm font-semibold">{a.newer.title}</div>
          </Link>
        )}
      </nav>
    </article>
  );
}
